import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { TOP_SELLERS_PERIOD_DESCRIPTIONS, TOP_SELLERS_PERIOD_EMPTY_MESSAGES } from '@/lib/dashboard'
import { formatNumber } from '@/lib/format'
import { cn } from '@/lib/utils'
import { TopSellersPeriodToggle } from './TopSellersPeriodToggle'
import type { TopSeller, TopSellersPeriod } from '@/api/types'

interface TopSellersCardProps {
  sellers: TopSeller[]
  period: TopSellersPeriod
  isLoading: boolean
  onPeriodChange: (period: TopSellersPeriod) => void
}

function TopSellerRow({ seller, rank }: { seller: TopSeller; rank: number }): React.ReactElement {
  return (
    <li className="flex items-center justify-between gap-4 border-b border-border/60 pb-2 last:border-b-0 last:pb-0">
      <span className="flex items-center gap-3">
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-medium text-primary">
          {rank}
        </span>
        <span className="font-medium">{seller.agent_name ?? 'Okänd säljare'}</span>
      </span>
      <span className="text-sm text-muted-foreground">{formatNumber(seller.customer_count)} st</span>
    </li>
  )
}

export function TopSellersCard({ sellers, period, isLoading, onPeriodChange }: TopSellersCardProps): React.ReactElement {
  const hasNoSellers = sellers.length === 0
  const contentClass = cn('transition-opacity duration-200', isLoading && 'opacity-50')

  return (
    <Card className="shadow-none border-border/80">
      <CardHeader>
        <CardTitle className="text-2xl font-serif text-foreground">Säljartopplista</CardTitle>
        <CardDescription>{TOP_SELLERS_PERIOD_DESCRIPTIONS[period]}</CardDescription>
        <CardAction>
          <TopSellersPeriodToggle
            period={period}
            onPeriodChange={onPeriodChange}
          />
        </CardAction>
      </CardHeader>
      <CardContent className={contentClass}>
        {hasNoSellers && <p className="text-sm text-muted-foreground">{TOP_SELLERS_PERIOD_EMPTY_MESSAGES[period]}</p>}

        {!hasNoSellers && (
          <ol className="space-y-2">
            {sellers.map((seller, index) => (
              <TopSellerRow
                key={seller.agent_id ?? index}
                seller={seller}
                rank={index + 1}
              />
            ))}
          </ol>
        )}
      </CardContent>
    </Card>
  )
}
